import clsx from 'clsx'
import {createElement, FocusEvent, forwardRef, HTMLProps, useState} from 'react'

export interface InputFieldProps extends HTMLProps<HTMLInputElement> {
  /**
   * Label of this input field.
   *
   * @default null
   */
  label?: string
  /**
   * Class name applied to input label.
   *
   * @default null
   */
  labelClassName?: string
  /**
   * Class name applied to input label and input field wrapper component.
   *
   * @default null
   */
  wrapperClassName?: string
  /**
   * Determines whether a text area should be rendered.
   *
   * @default false
   */
  multiline?: boolean
  /**
   * Text which should appear below the input field.
   *
   * @default null
   */
  hint?: string
  /**
   * Determines whether hint text should be displayed in error color.
   *
   * @default false
   */
  error?: boolean
}

export const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
  function InputField(
    {
      label,
      className,
      wrapperClassName,
      labelClassName,
      id,
      onFocus,
      onBlur,
      multiline,
      hint,
      error,
      ...props
    },
    ref,
  ) {
    const [focused, setFocused] = useState(false)

    function handleFocus(event: FocusEvent<HTMLInputElement>) {
      if (onFocus) {
        onFocus(event)
      }

      setFocused(true)
    }

    function handleBlur(event: FocusEvent<HTMLInputElement>) {
      if (onBlur) {
        onBlur(event)
      }

      setFocused(false)
    }

    return (
      <div className={clsx('grid grid-flow-row gap-1', wrapperClassName)}>
        {label && (
          <label
            htmlFor={id}
            className={clsx(
              focused && 'text-primary font-semibold',
              !focused && error && 'text-red-500',
              !focused && !error && 'text-typography-hint',
              'transition-colors justify-self-start',
              labelClassName,
            )}
          >
            {label}
            {props.required ? <span>*</span> : null}
          </label>
        )}

        {createElement(multiline ? 'textarea' : 'input', {
          id,
          className: clsx(
            'border-solid border-b-2 outline-none h-11 transition-colors rounded-none',
            focused && 'border-primary',
            !focused && error && 'border-red-500',
            !focused && !error && 'border-typography-hint',
            multiline && 'resize-none h-40 md:h-32',
            'w-full',
            className,
          ),
          onFocus: handleFocus,
          onBlur: handleBlur,
          ref,
          ...props,
        })}

        <span
          className={clsx(
            'min-h-5 text-sm',
            error ? 'text-red-500' : 'text-typography-hint',
          )}
        >
          {hint}
        </span>
      </div>
    )
  },
)

export default InputField
